import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { isEqual, uniqBy } from 'lodash';
import type ContractComparisonModel from './ContractComparisonModel';
import type ContractComparisonStore from '../../stores/ContractComparisonStore';
import { NodeUtils } from '../../misc';
import { getSectionDetails } from '../../misc/SectionDetails';
import {
    ConfirmContractComparisonSectionMatchesRequest,
    ContractComparisonSectionMatchDto,
    ContractComparisonSectionsResultDto,
    SectionPair
} from '../../types';

export default class SectionMatchingModel {
    readonly context: ContractComparisonSectionsResultDto['context'];

    readonly previousSections: ContractComparisonSectionsResultDto['previousSections'];

    confirmedMatches: ContractComparisonSectionMatchDto[] | null;

    draftMatches: ContractComparisonSectionMatchDto[] | null;

    expanded: boolean;

    private pendingRequest: ConfirmContractComparisonSectionMatchesRequest | null = null;

    constructor(
        private readonly comparison: ContractComparisonModel,
        private readonly store: ContractComparisonStore,
        data: ContractComparisonSectionsResultDto
    ) {
        this.context = data.context;
        this.previousSections = data.previousSections.filter(section => section.sectionId !== 'MainSection');
        this.confirmedMatches = this.isValidMapping(data.confirmedMatches) ? data.confirmedMatches : null;
        this.draftMatches = this.confirmedMatches === null ? this.suggestMatches() : null;
        this.expanded = true;

        makeObservable<SectionMatchingModel, 'pendingRequest'>(this, {
            confirmedMatches: observable,
            draftMatches: observable,
            expanded: observable,
            pendingRequest: observable.ref,
            newSections: computed,
            sectionPairs: computed,
            isConfirmed: computed,
            isEditing: computed,
            isConfirming: computed,
            canConfirm: computed,
            setMatch: action,
            cancelEdit: action,
            setExpanded: action,
            refresh: action,
            reset: action
        });
    }

    get newSections() {
        return uniqBy(
            this.comparison.newContractNodes.flatMap(node =>
                NodeUtils.getSections(node, this.comparison.parameters.layerId)
            ),
            'id'
        ).filter(section => section.id !== 'MainSection');
    }

    get sectionPairs(): readonly SectionPair[] {
        const pairs: SectionPair[] = [
            {
                id: 'MainSection',
                kind: 'matched',
                newSection: { id: 'MainSection', name: 'Main Section' },
                previousSection: { id: 'MainSection', name: 'Main Section', objectId: this.context.mainSectionObjectId }
            }
        ];

        const matchedPreviousIds = new Set<string>();

        this.newSections.forEach(section => {
            const match = this.isConfirmed
                ? this.confirmedMatches?.find(candidate => candidate.newSectionId === section.id)
                : undefined;

            const previous = this.previousSections.find(
                candidate => candidate.objectId === match?.previousSectionObjectId
            );

            const newSection = { id: section.id, name: section.name };

            if (previous) {
                matchedPreviousIds.add(previous.objectId);
                pairs.push({
                    id: `new:${section.id}`,
                    kind: 'matched',
                    newSection,
                    previousSection: { id: previous.sectionId, name: previous.name, objectId: previous.objectId }
                });
            } else {
                pairs.push({ id: `new:${section.id}`, kind: 'newOnly', newSection });
            }
        });

        this.previousSections
            .filter(section => !matchedPreviousIds.has(section.objectId))
            .forEach(section => {
                pairs.push({
                    id: `previous:${section.sectionId}`,
                    kind: 'previousOnly',
                    previousSection: { id: section.sectionId, name: section.name, objectId: section.objectId }
                });
            });

        return pairs;
    }

    get isConfirmed() {
        return this.isValidMapping(this.confirmedMatches);
    }

    get isEditing() {
        return this.draftMatches !== null;
    }

    getSectionDetails(sectionId: string) {
        return getSectionDetails(this.comparison.newContractNodes, sectionId, this.comparison.parameters.layerId);
    }

    get isConfirming() {
        return this.pendingRequest !== null;
    }

    get canConfirm() {
        return !this.isConfirming && this.isValidMapping(this.draftMatches);
    }

    setMatch(newSectionId: string, previousSectionObjectId: string | null) {
        if (this.isConfirming) {
            return;
        }

        let matches = this.draftMatches;

        // The first dropdown change starts a draft from the saved mapping; later changes keep that draft.
        if (matches === null && this.isConfirmed) {
            matches = this.confirmedMatches;
        }

        // Without a valid saved mapping, start from name-based suggestions for all current sections.
        if (matches === null) {
            matches = this.suggestMatches();
        }

        // Replace the edited match without mutating saved matches. Comparison uses them until confirmation.
        const updatedMatches = matches.map(match =>
            match.newSectionId === newSectionId ? { ...match, previousSectionObjectId } : match
        );
        // Edits preserve the applied mapping's order, even when it differs from the section catalog.
        this.draftMatches = this.isConfirmed && isEqual(updatedMatches, this.confirmedMatches) ? null : updatedMatches;

        this.expanded = true;
    }

    cancelEdit() {
        if (this.isConfirming) {
            return;
        }

        this.draftMatches = this.isConfirmed ? null : this.suggestMatches();
    }

    setExpanded(expanded: boolean) {
        this.expanded = expanded;
    }

    refresh() {
        if (this.pendingRequest) {
            if (this.isValidMapping(this.pendingRequest.matches)) {
                return;
            }

            this.pendingRequest = null;
        }

        // Field rebuilds keep rematching drafts while the confirmed section coverage remains valid.
        if (!this.isConfirmed) {
            this.confirmedMatches = null;
            this.draftMatches = this.suggestMatches();
            this.expanded = true;
        }
    }

    reset() {
        this.pendingRequest = null;
    }

    async confirm() {
        if (!this.canConfirm || !this.draftMatches) {
            return undefined;
        }

        const request = { context: this.context, matches: [...this.draftMatches] };

        runInAction(() => {
            this.pendingRequest = request;
        });

        try {
            const response = await this.store.confirmSectionMatches(request);

            if (!this.isCurrentRequest(request) || !this.isValidMapping(request.matches)) {
                return undefined;
            }

            if (!response.isOk()) {
                return false;
            }

            runInAction(() => {
                this.confirmedMatches = request.matches;
                this.draftMatches = null;
            });

            return true;
        } catch (error) {
            if (this.isCurrentRequest(request)) {
                return false;
            }

            return undefined;
        } finally {
            if (this.isCurrentRequest(request)) {
                runInAction(() => {
                    this.pendingRequest = null;
                });
            }
        }
    }

    private isCurrentRequest(request: ConfirmContractComparisonSectionMatchesRequest) {
        return this.pendingRequest === request && this.comparison.sectionMatching === this;
    }

    private isValidMapping(matches: ContractComparisonSectionMatchDto[] | null) {
        if (!matches || matches.length !== this.newSections.length) {
            return false;
        }

        const newIds = matches.map(match => match.newSectionId);
        const previousIds = matches.flatMap(match =>
            match.previousSectionObjectId === null ? [] : [match.previousSectionObjectId]
        );

        return (
            new Set(newIds).size === newIds.length &&
            this.newSections.every(section => newIds.includes(section.id)) &&
            new Set(previousIds).size === previousIds.length &&
            previousIds.every(id => this.previousSections.some(section => section.objectId === id))
        );
    }

    private suggestMatches(): ContractComparisonSectionMatchDto[] {
        return this.newSections.map(section => {
            const name = section.name.trim().toLowerCase();

            const matchingNewSections = this.newSections.filter(
                candidate => candidate.name.trim().toLowerCase() === name
            );

            const matchingPreviousSections = this.previousSections.filter(
                candidate => candidate.name.trim().toLowerCase() === name
            );

            return {
                newSectionId: section.id,
                previousSectionObjectId:
                    name && matchingNewSections.length === 1 && matchingPreviousSections.length === 1
                        ? matchingPreviousSections[0].objectId
                        : null
            };
        });
    }
}
